import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowUp, X } from 'lucide-react';
import { useLang } from '../i18n';

const LABELS = {
  title: { en: 'Ask AI', fr: 'Poser une question' },
  placeholder: { en: 'Ask about the work…', fr: 'Posez votre question…' },
  greeting: {
    en: 'Hello — ask me anything about Donatien Grimaud’s work, chapters and career.',
    fr: 'Bonjour — posez vos questions sur l’univers, les chapitres et le parcours de Donatien Grimaud.',
  },
  error: { en: 'The concierge is unavailable — please try again later.', fr: 'Le concierge est indisponible — veuillez réessayer plus tard.' },
};

function sessionId() {
  let id = localStorage.getItem('dg-chat-session');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('dg-chat-session', id);
  }
  return id;
}

export default function AskAI() {
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: LABELS.greeting[lang] }]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const listRef = useRef(null);
  const sId = useRef(sessionId());

  useEffect(() => {
    if (!open) return;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/history/${sId.current}`)
      .then((r) => r.json())
      .then((d) => {
        if (d.messages && d.messages.length) {
          setMessages([{ role: 'assistant', content: LABELS.greeting[lang] }, ...d.messages]);
        }
      })
      .catch(() => {});
  }, [open, lang]);

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setMessages((m) => [...m, { role: 'user', content: text }, { role: 'assistant', content: '' }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/chat/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: sId.current, message: text, lang }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buffer = '';
      let done = false;
      while (!done) {
        const { value, done: d } = await reader.read();
        if (d) break;
        buffer += dec.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const payload = line.slice(6);
          if (payload === '[DONE]') { done = true; break; }
          try {
            const evt = JSON.parse(payload);
            if (evt.delta) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: 'assistant', content: c[c.length - 1].content + evt.delta };
                return c;
              });
            }
            if (evt.error) {
              setMessages((m) => {
                const c = [...m];
                c[c.length - 1] = { role: 'assistant', content: t(LABELS.error) };
                return c;
              });
            }
          } catch (e) {}
        }
      }
    } catch (e) {
      setMessages((m) => {
        const c = [...m];
        c[c.length - 1] = { role: 'assistant', content: t(LABELS.error) };
        return c;
      });
    }
    setBusy(false);
  };

  useEffect(() => {
    window.__askAI = { open: () => setOpen(true) };
    return () => { delete window.__askAI; };
  }, []);

  return createPortal(
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 16 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            data-testid="ask-ai-panel"
            className="fixed bottom-20 right-4 z-[80] flex h-[520px] max-h-[75vh] w-[380px] max-w-[calc(100vw-2rem)] flex-col border border-ink/15 bg-paper shadow-lg md:bottom-24 md:right-8"
          >
            <div className="flex items-center justify-between border-b border-ink/10 px-5 py-4">
              <span className="text-[10px] uppercase tracking-[0.3em]">{t(LABELS.title)}</span>
              <button onClick={() => setOpen(false)} data-testid="ask-ai-close" aria-label="Close" className="text-ink/50 transition-colors hover:text-ink">
                <X size={18} strokeWidth={1.25} />
              </button>
            </div>

            <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-5 py-5" data-testid="ask-ai-messages">
              {messages.map((m, i) => (
                <div key={i} data-testid={`ask-ai-message-${m.role}`} className={m.role === 'user' ? 'text-right' : ''}>
                  <div
                    className={`inline-block max-w-[85%] px-4 py-3 text-left text-[13px] leading-relaxed ${
                      m.role === 'user' ? 'bg-ink text-paper' : 'border border-ink/10 text-ink/85'
                    }`}
                  >
                    {m.content || '…'}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center gap-3 border-t border-ink/10 px-4 py-3">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && send()}
                placeholder={t(LABELS.placeholder)}
                data-testid="ask-ai-input"
                className="flex-1 bg-transparent py-2 text-sm font-light outline-none placeholder:text-ink/40"
              />
              <button
                onClick={send}
                disabled={busy || !input.trim()}
                data-testid="ask-ai-send"
                aria-label="Send"
                className="flex h-9 w-9 items-center justify-center border border-ink/30 text-ink transition-colors duration-300 hover:bg-ink hover:text-paper disabled:opacity-30"
              >
                <ArrowUp size={15} strokeWidth={1.5} />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>,
    document.body
  );
}
